import React from 'react'
import { commonCtaText } from 'src/modules/utils/constants'
import { networkLogos } from '../networkLogos'
import { ratePlanCodes } from '../ratePlanCode'
import { createNetworkLogo, formatListText } from 'src/modules/utils/plans'
import { COMMON_FEATURES, PlanIntervals } from './index'
import { channelIds } from 'src/modules/hooks/useChannelSelector'
import { LA_COMMON_LIST_ITEMS } from './latinoPlanData'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { addOnCodes } from '../addOnCodes'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { body } from '../marketing-ds/typography'

const price = {
  pro: {
    [PlanIntervals.monthly]: { price: 73.99, discountedPrice: 48.99 },
  },
  essential: {
    [PlanIntervals.monthly]: { price: 84.99, discountedPrice: 59.99 },
  },
  elite: {
    [PlanIntervals.monthly]: { price: 83.99, discountedPrice: 53.99 },
  },
}

// channelId is used to get the correct channel list, used in hooks/useChannelSelector.js
const baseData = {
  pro_monthly: {
    market: 'us',
    offerCode: ratePlanCodes.pro,
    plan: 'pro',
    channelId: channelIds.pro,
    title: 'Fubo Pro',
    savingsNote: 'Save $25',
    backgroundColor: '#ff3b04',
    listItems: (data) => [
      { available: true, text: `${data.channels}+ live TV channels` },
      { available: true, text: 'ESPN Unlimited included' },
      { available: false, text: 'MLB Network, NBA TV & more' },
      { available: false, text: '4K channels' },
      { available: true, text: 'Unlimited Cloud DVR' },
      { available: true, text: 'Stream on 10 screens at home' },
      { available: false, text: 'Fubo VIP' },
    ],
    subText: 'Plus taxes and additional fees.',
    asterisk: true,
    interval: PlanIntervals.monthly,
    price: price.pro.monthly.price,
    discountedPrice: price.pro.monthly.discountedPrice,
    networkLogos: [
      createNetworkLogo(networkLogos.abc, 27, 27),
      createNetworkLogo(networkLogos.cbs, 42, 12),
      createNetworkLogo(networkLogos.fox, 31, 13),
      createNetworkLogo(networkLogos.espn, 45, 11),
      {
        alt: '+ MORE',
      },
    ],
    footnote: (data) => (
      <>
        *${data.discountedPrice} for month 1, ${data.price}/mo thereafter. Regional restrictions
        apply. <br />
        Additional taxes and fees may apply. Prices subject to change.
      </>
    ),
  },
  essential_monthly: {
    market: 'us',
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/card-og-receiver-cropped.png?w=600&ch=width&auto=format,compress',
    offerCode: ratePlanCodes.essential,
    plan: 'essential',
    channelId: channelIds.essential,
    title: 'Fubo Essential',
    subtitle: 'Best for football and entertainment fans',
    subText: (
      <>
        Plus taxes. <span style={{ textDecoration: 'underline' }}> No additional fees. </span>
      </>
    ),
    interval: PlanIntervals.monthly,
    price: price.essential.monthly.price,
    discountedPrice: price.essential.monthly.discountedPrice,
    listItems: (data) => [
      { available: false, text: 'No local NBA, NHL or MLB games' },
      { available: true, text: 'Includes national NFL games' },
      { available: true, text: `${data.channels}+ top TV channels` },
      { available: true, text: 'Unlimited Cloud DVR' },
      { available: true, text: 'Watch on 3 devices at once' },
    ],
    asterisk: true,
    footnote: () =>
      `$${price.essential.discountedPrice} for first month, $${price.essential.price}/mo thereafter. Regional restrictions apply.`,
  },
  elite_monthly: {
    market: 'us',
    bannerImage:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/elite-card-redzone-banner.png',
    offerCode: ratePlanCodes.elite,
    plan: 'elite',
    channelId: channelIds.elite,
    title: <>Fubo Elite</>,
    savingsNote: 'Save $30',
    interval: PlanIntervals.monthly,
    price: price.elite.monthly.price,
    discountedPrice: price.elite.monthly.discountedPrice,
    networkLogos: [
      createNetworkLogo(networkLogos.abc, 27, 27),
      createNetworkLogo(networkLogos.cbs, 42, 12),
      createNetworkLogo(networkLogos.fox, 31, 13),
      createNetworkLogo(networkLogos.espn, 45, 11),
      {
        alt: '+ MORE',
      },
    ],
    listItems: (data) => [
      { available: true, text: `${data.channels}+ live TV channels` },
      { available: true, text: 'ESPN Unlimited included' },
      { available: true, text: 'MLB Network, NBA TV & more' },
      { available: true, text: '4K channels' },
      { available: true, text: 'Unlimited Cloud DVR' },
      { available: true, text: 'Stream on 10 screens at home' },
      { available: true, text: 'Fubo VIP' },
    ],
    asterisk: true,
    footnote: (data) => (
      <>
        *${data.discountedPrice} for month 1, ${data.price}/mo thereafter. Regional restrictions
        apply. <br />
        Additional taxes and fees may apply. Prices subject to change.
      </>
    ),
  },

  us_standalone_monthly: {
    market: 'us',
    channelId: channelIds.us_standalone,
    interval: PlanIntervals.monthly,
    listItems: (data) => [
      {
        available: true,
        text: 'Watch the ' + formatListText(data?.teams),
      },
      { available: true, text: `${data.channels}+ free channels of live TV` },
      { available: true, text: COMMON_FEATURES.CLOUD_DVR },
      { available: true, text: COMMON_FEATURES.MULTI_SCREEN },
      { available: false, text: COMMON_FEATURES.NFL_GAMES },
      // TEMPORARY NBC DISPUTE
      // { available: false, text: 'ABC, CBS, FOX, NBC, ESPN & more' },
      { available: false, text: 'ABC, CBS, FOX, ESPN & more' },
      {},
    ],
    asterisk: false,
    footnote: (data) => (
      <>
        {/* Only display the entire price line if discountedPrice is defined, including the thereafter price and the line break  */}
        {data?.discountedPrice != null && (
          <>
            *${data.discountedPrice} for first month, ${data.price}/mo thereafter. <br />
          </>
        )}
        Regional restrictions apply. Additional taxes and fees may apply.
        <br />
        Prices subject to change.
      </>
    ),
  },

  fubo_sports_monthly: {
    title: 'Fubo Sports',
    titleLogo: 'https://assets-imgx.fubo.tv/marketing/lp/images/logos/Fubo-Sports.png',
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/sports_plan_card_bg.png',
    offerCode: ratePlanCodes.fubo_sports_monthly,
    plan: 'fubo_sports',
    subtitle: 'Football & more sports for less',
    price: 55.99,
    discountedPrice: 45.99,
    savingsNote: 'Save $10',
    banner: '“The best value for football fans” –Engadget',
    bannerBgColor: '#4F4FFC',
    listItems: (data) => [
      { available: true, text: '25+ channels for sports fans' },
      { available: true, text: 'NFL, MLB, NHL, NBA' },
      { available: true, text: 'ESPN Unlimited included' },
      { available: true, text: 'Stream on 10 screens at once' },
      { available: true, text: 'ABC, CBS, FOX' },
      // { available: false, text: 'NBC' },
      // TEMPORARY NBC DISPUTE
    ],
    footnote: (data) =>
      `*$${data.discountedPrice} for month 1, $${data.price}/mo thereafter.
Regional restrictions apply. Additional taxes and fees may apply.
Prices subject to change.
`,
    footnoteInCard: (data) => {
      let ctaURL = replaceUrlParam(defaultCTAURI, 'offer', ratePlanCodes.fubo_sports_monthly)
      ctaURL = replaceUrlParam(ctaURL, 'addons[]', addOnCodes.fubo_sports_redzone)
      ctaURL = replaceUrlParam(ctaURL, 'comp', 'plan-card-footnote')

      return (
        <a style={{ textDecoration: 'underline', ...body.sm, padding: '10px' }} href={ctaURL}>
          Add NFL RedZone for $9.99/mo
        </a>
      )
    },
  },
}

// Finally create the combined plans
// When creating a new plan with no base plan copy, always include the interval property:
// Example: interval: PlanIntervals.monthly
// This ensures the plan is recognized with the correct billing interval.
export const usPlanData = {
  pro_monthly: baseData.pro_monthly,
  essential_monthly: baseData.essential_monthly,
  elite_monthly: baseData.elite_monthly,
  fubo_sports_monthly: baseData.fubo_sports_monthly,
  pro_soccer_en_monthly: {
    ...baseData.pro_monthly,
    subtitle: null,
    savingsNote: null,
    listItems: (data) => [
      { available: true, text: 'Soccer matches in English & Spanish' },
      { available: true, text: `${data.channels}+ channels of live TV & sports` },
      { available: true, text: 'Local MLB, NBA and NHL games' },
      { available: true, text: 'Unlimited Cloud DVR' },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.cashApp },
      { available: true, text: 'ESPN Unlimited included' },
    ],
    networkLogos: [
      createNetworkLogo(networkLogos.espn, 45, 19),
      {
        logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/networks/cbs-sports-stacked-m.png',
        alt: 'CBS Sports Network',
        width: 70,
        height: 30,
      },
      createNetworkLogo(networkLogos.fs1, 35, 35),
      // TEMPORARY NBC DISPUTE
      // createNetworkLogo(networkLogos.usa, 35, 35),
      createNetworkLogo(networkLogos.bein, 75, 19),
      {
        alt: '+MORE',
      },
    ],
    footnote: (data) =>
      `*Offer valid for the first month. Renews at $${data.price}/mo. Regional restrictions apply. Additional taxes and fees may apply. Prices subject to change. Cancel anytime.`,
  },
  pro_soccer_es_monthly: {
    ...baseData.pro_monthly,
    subtitle: null,
    savingsNote: null,
    networkLogos: [
      createNetworkLogo(networkLogos.espn, 45, 19),
      {
        logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/networks/cbs-sports-stacked-m.png',
        alt: 'CBS Sports Network',
        width: 70,
        height: 30,
      },
      createNetworkLogo(networkLogos.fs1, 35, 35),
      // TEMPORARY NBC DISPUTE
      // createNetworkLogo(networkLogos.usa, 35, 35),
      createNetworkLogo(networkLogos.bein, 75, 19),
      {
        alt: 'Y MÁS',
      },
    ],
    listItems: (data) => [
      { available: true, text: 'Partidos de fútbol en inglés y español' },
      { available: true, text: `Más de ${data.channels} canales de TV y deportes en vivo` },
      { available: true, text: 'Partidos locales de MLB, NBA y NHL' },
      { available: true, text: 'Espacio ilimitado en tu Cloud DVR' },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.cashApp },
      { available: true, text: 'Incluye ESPN Unlimited' },
    ],
    footnote: (data) =>
      `*Oferta válida solo para el primer mes. Se renueva a $${data.price}/mes. Se aplican restricciones regionales. Pueden aplicarse impuestos y otros cargos. Precios sujetos a cambios. Cancela cuando quieras.`,
  },

  fanduel_standalone_monthly: {
    ...baseData.us_standalone_monthly,
    title: 'FanDuel Sports Network',
    plan: 'fanduel sports network',
    ctaText: commonCtaText.startWatchingForFree,
  },
  pro_standalone_monthly: {
    ...baseData.pro_monthly,
    promoImg: null,
    subtitle: null,
    asterisk: true,
    savingsNote: null,
    backgroundColor: '#ff3b04',
    fillImageSpace: true,
    networkLogos: [
      createNetworkLogo(networkLogos.abc, 38, 38),
      createNetworkLogo(networkLogos.fox, 55, 24),
      createNetworkLogo(networkLogos.nflnetwork, 71, 49),
      createNetworkLogo(networkLogos.espn, 75, 19),
      {
        alt: '+ More',
      },
    ],
    listItems: (data) => [
      {
        available: true,
        text: 'Watch the ' + formatListText(data?.teams),
      },
      { available: true, text: `${data.channels}+ top channels of live TV` },
      { available: true, text: COMMON_FEATURES.CLOUD_DVR },
      { available: true, text: COMMON_FEATURES.MULTI_SCREEN },
      { available: true, text: COMMON_FEATURES.NFL_GAMES },
      // TEMPORARY NBC DISPUTE
      // { available: true, text: 'ABC, CBS, FOX, NBC, ESPN & more' },
      { available: true, text: 'ABC, CBS, FOX, ESPN & more' },
      { available: true, text: 'ESPN+ included' },
    ],
    footnote: (data) => baseData.us_standalone_monthly.footnote(data),
  },
  rangers_sports_network_monthly: {
    ...baseData.us_standalone_monthly,
    rsn: ['Rangers Sports Network'],
    teams: ['Texas Rangers'],
    title: 'Rangers Sports Network',
    offerCode: ratePlanCodes.rangers_sports_network,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/fanduel-poster-plan-card.png',
    price: 20,
    networkLogos: [
      {
        logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/networks/rangers_sports_network-red-logo.png',
        alt: 'Rangers Sports Network',
        width: 100,
        height: 48,
      },
    ],
    ctaText: commonCtaText.startWatchingForFree,
  },
  marquee_monthly: {
    ...baseData.us_standalone_monthly,
    offerCode: ratePlanCodes.marquee_mo,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/fanduel-poster-plan-card.png',
    title: 'Marquee Sports Network',
    plan: 'marquee sports network',
    price: 19.99,
    discountedPrice: 14.99,
    teams: ['Chicago Cubs', 'Sky', 'Red Stars'],
    networkLogos: [createNetworkLogo(networkLogos.marquee, 85, 48)],
    ctaText: commonCtaText.startWatchingForFree,
  },
  chicago_sports_network_monthly: {
    ...baseData.us_standalone_monthly,
    offerCode: ratePlanCodes.chicago_sports_network_mo,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/fanduel-poster-plan-card.png',
    title: 'Chicago Sports Network',
    plan: 'chicago sports network',
    price: 29.99,
    discountedPrice: 24.99,
    teams: ['Chicago Bulls', 'Chicago Blackhawks', 'Chicago White Sox'],
    networkLogos: [createNetworkLogo(networkLogos.chsn, 100, 48)],
    ctaText: commonCtaText.startWatchingForFree,
  },
  masn_plus_network_monthly: {
    ...baseData.us_standalone_monthly,
    offerCode: ratePlanCodes.masn_plus,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/us/fanduel-poster-plan-card.png',
    title: 'MASN+',
    plan: 'masn plus',
    price: 19.99,
    discountedPrice: 14.99,
    teams: ['Baltimore Orioles', 'Washington Nationals'],
    networkLogos: [createNetworkLogo(networkLogos.masn, 85, 48)],
    ctaText: commonCtaText.startWatching,
  },
}
